import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import { List } from "@/components/list/List";
import { paginationStandardProps, selectStandardProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { numberOrZero, replaceInArray } from "@/utils/utils";
import { validationConstants, validationFunctions, validations } from "@/utils/validationUtils";
import { App as AntApp, Button, Checkbox, Col, Form, Input, Popconfirm, Row, Select } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFilterVehicleBrands, useUpdateVehicleBrandMappings } from "../../../../../common/api/queries";
import HiddenInput from "../../../../../common/components/form/components/HiddenInput";
import AntIcon from "../../../../../common/components/icons/AntIcon";
import PopconfirmDeleteIcon from "../../../../../common/components/icons/PopconfirmDeleteIcon";
import { PageSizes, rowGutter } from "../../../../../common/constants";
import { RootArrayFormObject } from "../../../../../common/types";
import type { UUID } from "../../../../../typings/global";
import { VehicleMappingType } from "../../enums";
import { UpdateVehicleMapping, UpdateVehicleMappingFormItem, VehicleMapping, VehicleMappingData } from "../../types";
import {
  parseVehicleMappingsFromIdentifiers,
  parseVehicleMappingsToUpdateFormItems,
  VEHICLE_IDENTIFIER_DELIMITER,
  VEHICLE_TYPES_PATH,
} from "../../utils";

interface Props {
  selectedMappingTypes: VehicleMappingType[];
  mappingData: VehicleMappingData[];
  onShowModelsClick: (brandMappingId: UUID) => void;
}

const VehicleBrandMappingsListForm = ({ selectedMappingTypes, mappingData, ...props }: Props) => {
  const { t } = useTranslation();
  const { message, modal } = AntApp.useApp();

  const [form] = Form.useForm<RootArrayFormObject<UpdateVehicleMappingFormItem>>();
  const updateVehicleBrandMappings = useUpdateVehicleBrandMappings();

  const [updatedRowsIds, setUpdatedRowsIds] = useState<UUID[]>([]);
  const [keyword, setKeyword] = useState<string>();
  const [excludeMapped, setExcludeMapped] = useState<boolean>(false);
  const [pageIndex, setPageIndex] = useState<number>(0);

  const { data: currentPage } = useFilterVehicleBrands({
    keyword: keyword,
    pageIndex: pageIndex,
    pageSize: PageSizes.SMALL,
    queriedMappings: selectedMappingTypes,
    excludeMapped: excludeMapped,
  });

  useEffect(() => {
    if (currentPage?.pageData) {
      form.setFieldsValue({
        items: parseVehicleMappingsToUpdateFormItems(...currentPage.pageData),
      });
    }

    setUpdatedRowsIds([]);
  }, [currentPage, form]);

  const handleFormFinish = (values: RootArrayFormObject<UpdateVehicleMappingFormItem>) => {
    if (updatedRowsIds.length > 0) {
      updateVehicleBrandMappings.mutate(
        {
          mappings: values.items
            .filter((mapping) => updatedRowsIds.includes(mapping.id))
            .map<UpdateVehicleMapping>(
              (mapping) => parseVehicleMappingsFromIdentifiers(mapping) as UpdateVehicleMapping,
            ),
        },
        { onError: (error) => setErrorsToForm(form, "vehicle.attrs", error.violations) },
      );
    } else {
      message.warning(t("vehicle.validations.noChanges"));
    }
  };

  const handleSearchSubmit = (keyword: string) => {
    if (validationFunctions.validateSearchKeyword(keyword)) {
      checkUnsavedChangesBeforePageChange(() => {
        setKeyword(keyword);
        setPageIndex(0);
      });
    } else {
      message.error(
        t("validation.size", {
          min: validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
          max: validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
        }),
      );
    }
  };

  const handleExcludeMappedChange = (e: CheckboxChangeEvent) => {
    checkUnsavedChangesBeforePageChange(() => {
      setExcludeMapped(e.target.checked);
      setPageIndex(0);
    });
  };

  const handleListPageChange = (pageNumber: number) => {
    checkUnsavedChangesBeforePageChange(() => setPageIndex(pageNumber - 1));
  };

  const handleMappingRowChange = (id: UUID) => {
    if (!updatedRowsIds.includes(id)) {
      setUpdatedRowsIds([...updatedRowsIds, id]);
    }
  };

  const handleRowResetClick = (rowId: UUID) => {
    const row = currentPage?.pageData.find((row) => row.id === rowId);
    if (!row) {
      return;
    }

    const allRows = form.getFieldsValue().items;
    const newValue = parseVehicleMappingsToUpdateFormItems(row)[0];

    if (newValue) {
      const items = replaceInArray<UpdateVehicleMappingFormItem>(
        allRows,
        (item) => item?.id === rowId,
        () => newValue,
      );
      form.setFieldsValue({
        items: items,
      });
      setUpdatedRowsIds(updatedRowsIds.filter((stateRowId) => stateRowId !== rowId));
    }
  };

  const checkUnsavedChangesBeforePageChange = (callback: () => void) => {
    if (updatedRowsIds.length > 0) {
      modal.confirm({
        title: t("common.unsavedChanges"),
        okText: t("common.continue"),
        cancelText: t("common.back"),
        onOk: callback,
      });
    } else {
      callback();
    }
  };

  return (
    <>
      <Row gutter={rowGutter} className="margin-bottom-small">
        <Col span={16}>
          <Input.Search
            style={{ width: 350 }}
            enterButton
            allowClear
            defaultValue={currentPage?.keyword}
            placeholder={t("vehicle.helpers.searchBrandHint")}
            onSearch={handleSearchSubmit}
          />

          <Checkbox
            className="margin-left-small"
            style={{ marginTop: 4 }}
            checked={currentPage?.excludeMapped}
            onChange={handleExcludeMappedChange}
          >
            {t("vehicle.actions.excludeMapped")}
          </Checkbox>
        </Col>
      </Row>

      <Form form={form} layout="vertical" onFinish={handleFormFinish} name="vehicleBrandMappingsListForm">
        <List<VehicleMapping>
          items={currentPage?.pageData ?? []}
          gap={12}
          split
          pagination={{
            ...paginationStandardProps,
            mode: "server",
            current: numberOrZero(currentPage?.pageIndex) + 1,
            pageSize: currentPage?.pageSize,
            total: numberOrZero(currentPage?.totalElementsCount),
            onChange: handleListPageChange,
          }}
          renderItem={(item, rowIndex) => (
            <div style={{ paddingTop: 24 }}>
              <HiddenInput name={["items", rowIndex, "id"]} />
              <HiddenInput name={["items", rowIndex, "optimisticLockVersion"]} />

              <Row
                gutter={rowGutter}
                style={{ backgroundColor: updatedRowsIds.includes(item.id) ? "lightblue" : "inherit" }}
              >
                <Col span={4}>
                  <Form.Item
                    name={["items", rowIndex, "name"]}
                    label={t("vehicle.attrs.name")}
                    rules={[validations.notBlank]}
                  >
                    <Input size="small" onChange={() => handleMappingRowChange(item.id)} />
                  </Form.Item>
                </Col>

                <Col span={16}>
                  <Row gutter={rowGutter}>
                    {Object.values(VehicleMappingType).map((type) => {
                      const typePath = VEHICLE_TYPES_PATH[type];
                      const mapping = mappingData.find((mapping) => mapping.type === type);

                      return (
                        <React.Fragment key={type}>
                          <HiddenInput name={["items", rowIndex, typePath.keyId]} />
                          <HiddenInput name={["items", rowIndex, typePath.keyName]} />

                          {selectedMappingTypes.includes(type) ? (
                            <Col span={6} key={type}>
                              <Form.Item
                                name={["items", rowIndex, typePath.keyIdentifier]}
                                label={t(`vehicle.enums.vehicleMappingType.${type}`)}
                                rules={[validations.none]}
                              >
                                <Select
                                  {...selectStandardProps}
                                  allowClear
                                  size="small"
                                  options={(mapping && mapping.brands ? mapping.brands : []).map((item) => ({
                                    value: item.id + VEHICLE_IDENTIFIER_DELIMITER + item.name,
                                    label: `${item.name} (${item.id})`,
                                  }))}
                                  onChange={() => handleMappingRowChange(item.id)}
                                />
                              </Form.Item>
                            </Col>
                          ) : (
                            <HiddenInput name={["items", rowIndex, typePath.keyIdentifier]} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Row>
                </Col>

                <Col span={4} className="right-align">
                  <div className="margin-bottom-tiny">
                    <SecondaryButton
                      size="small"
                      icon={<AntIcon type="ordered-list" />}
                      style={{ width: 100 }}
                      onClick={() => props.onShowModelsClick(item.id)}
                    >
                      {t("vehicle.actions.showModels")}
                    </SecondaryButton>
                  </div>
                  <br />

                  <Button
                    size="small"
                    icon={<AntIcon type="reload" />}
                    className="margin-bottom-tiny"
                    style={{ width: 100 }}
                    onClick={() => handleRowResetClick(item.id)}
                  >
                    {t("common.reset")}
                  </Button>
                  <br />

                  <Popconfirm
                    title={t("vehicle.titles.brandDelete")}
                    icon={<PopconfirmDeleteIcon />}
                    okText={t("common.yes")}
                    cancelText={t("common.no")}
                    okType="danger"
                    disabled
                  >
                    <Button
                      size="small"
                      className="margin-bottom-tiny"
                      disabled
                      danger
                      icon={<AntIcon type="delete" />}
                      style={{ width: 100 }}
                    >
                      {t("common.delete")}
                    </Button>
                  </Popconfirm>
                </Col>
              </Row>
            </div>
          )}
        />

        <Button className="margin-top-medium" type="primary" htmlType="submit" icon={<AntIcon type="save" />}>
          {t("common.save")}
        </Button>
      </Form>
    </>
  );
};

export default VehicleBrandMappingsListForm;
